import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Heading3, Flex } from './ui';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { TourismAPI, API_BASE_URL } from '../api/tourism';
import { ItineraryAPI, CombinedItinerary } from '../api/itinerary';
import AppHeader from './Header';
import CinematicHero from './ui/CinematicHero';
import FlipCard, { FlipCardSkeleton } from './FlipCard';
import EnquiryDialog from './EnquiryDialog';
import { useHeaderItineraries } from '../hooks/useHeaderItineraries';
import { useAutoFlipCardsGrid } from '../hooks/useAutoFlipCardsGrid';

interface Destination {
  id: number;
  title: string;
  description: string;
  category: string;
  duration: string;
  image: string;
  destinations: string[];
  state: string;
}

interface CuratedExperiencesProps {
  destination: Destination | null;
  onBack: () => void;
  onNavigateTo?: (page: string, data?: any) => void;
  tourType?: TourTypeFilter;
}

type StateFilter = 'all' | 'Ladakh' | 'Kashmir' | 'Himachal';
type TourTypeFilter = 'all' | 'car' | 'bike' | 'trek';

const PageContainer = styled.div`
  background: #FFFFFF;
  color: ${getColor('primary.900')};
  font-family: ${DesignSystem.typography.fonts.primary};
  line-height: 1.6;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
`;



const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${getSpacing('lg')};
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 0 ${getSpacing('md')};
  }
  
  @media (max-width: 480px) {
    padding: 0 ${getSpacing('sm')};
  }
`;



const ContentWrapper = styled.div`
  padding-top: ${getSpacing('xl')};
  width: 100%;
  
  @media (max-width: 768px) {
    padding-top: ${getSpacing('lg')};
  }
`;

const SectionContainer = styled.section`
  margin-bottom: ${getSpacing('xl')};
  width: 100%;
  
  @media (max-width: 768px) {
    margin-bottom: ${getSpacing('lg')};
  }
`;

// Grid container for cards - now using flexbox for horizontal adventure-style layout
const CardGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: ${getSpacing('md')} 0;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 1200px) {
    gap: 16px;
  }
  
  @media (max-width: 768px) {
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

// FlipCard styled components moved to FlipCard.tsx

const LocationsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('xs')};
`;

const LocationItem = styled.li`
  font-size: 0.75rem;
  color: ${getColor('primary.700')};
  padding: 4px 8px;
  background: ${getColor('primary.100')};
  border-radius: 6px;
`;

// Override CinematicHero title/subtitle colors to black (gold spans preserved)
const DarkTextHeroWrapper = styled.div`
  /* Match HomePage hero: vh-based padding, top-aligned content */
  section {
    padding-top: 15vh;
    padding-bottom: 15vh;
    justify-content: flex-start;
  }
  /* Hero title: tight gap to subtitle (matches HomePage 1.125vh) */
  section > div > h1 {
    color: #ffffff;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    margin-bottom: 1.125vh;
  }
  /* Hero subtitle */
  section > div > p {
    color: rgba(255, 255, 255, 0.85);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    font-style: italic;
  }
  /* Dark overlay for white text readability */
  section::before {
    background: rgba(0, 0, 0, 0.35) !important;
  }
  
  @media (max-width: 768px) {
    section {
      padding-top: 13vh;
      padding-bottom: 13vh;
    }
    section > div > h1 {
      margin-bottom: 0.975vh;
    }
  }
  
  @media (max-width: 480px) {
    section {
      padding-top: 11vh;
      padding-bottom: 11vh;
    }
    section > div > h1 {
      margin-bottom: 0.825vh;
    }
  }
`;

// Wrapper for filters + results info inside the CinematicHero
const HeroFilterWrapper = styled.div`
  position: relative;
  z-index: 3;
  width: 100%;
  max-width: 1200px;
  padding: 0 ${getSpacing('lg')};
  flex: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media (max-width: 768px) {
    padding: 0 ${getSpacing('md')};
  }
  
  @media (max-width: 480px) {
    padding: 0 ${getSpacing('sm')};
  }
`;

// Centers filter rows vertically in the middle zone between title/subtitle and results
const FiltersGroup = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 30px;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 24px;
  }
  
  @media (max-width: 480px) {
    gap: 20px;
  }
`;

// Results info shown inside the hero — pinned to bottom of wrapper
const HeroResultsInfo = styled.div`
  margin-bottom: 0;
  margin-top: 0;
`;

const HeroResultsTitle = styled.h2`
  font-size: 1.65rem;
  font-weight: 400;
  color: #ffffff;
  margin: 0 0 6px 0;
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.01em;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const HeroResultsSubtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
  line-height: 1.6;
  font-weight: 400;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  span {
    color: #CAB96E;
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

// Scroll hint animations
const fadeInHint = `
  @keyframes fadeInHint {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const bounceDown = `
  @keyframes bounceDown {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(8px); }
  }
`;

const ScrollHintWrapper = styled.div`
  ${fadeInHint}
  ${bounceDown}
  margin-top: 1.125vh;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  
  &:hover {
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    margin-top: 0.975vh;
  }
  
  @media (max-width: 480px) {
    margin-top: 1.5vh;
  }
`;

const ScrollHintText = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: #CAB96E;
  text-transform: uppercase;
  letter-spacing: 0.25em;
  opacity: 0.6;
  animation: fadeInHint 1.5s ease-in-out;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.65rem;
    letter-spacing: 0.15em;
  }
`;

const BounceChevron = styled.span`
  color: #CAB96E;
  opacity: 0.5;
  font-size: 1.2rem;
  line-height: 1;
  animation: bounceDown 2s ease-in-out infinite;
`;

// Single horizontal row for all filter pills
const FilterRow = styled.div`
  display: inline-flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

// Vertical divider between filter groups
const FilterDivider = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 8px;
  
  @media (max-width: 768px) {
    margin: 0 4px;
    height: 20px;
  }
`;

// Inline label for each filter group
const FilterGroupLabel = styled.span`
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-right: 4px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  min-width: 110px;
  text-align: right;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    min-width: unset;
    text-align: center;
  }
`;

const FilterButton = styled.button<{ isActive: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${p => p.isActive ? '#CAB96E' : 'rgba(255, 255, 255, 0.35)'};
  background: ${p => p.isActive ? '#CAB96E' : 'transparent'};
  color: ${p => p.isActive ? '#1a1a1a' : '#ffffff'};
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  text-shadow: ${p => p.isActive ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.3)'};
  
  &:hover {
    border-color: ${p => p.isActive ? '#CAB96E' : 'rgba(255, 255, 255, 0.5)'};
    ${p => !p.isActive && 'background: rgba(255, 255, 255, 0.1);'}
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.75rem;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    padding: 5px 10px;
    font-size: 0.75rem;
    min-height: 44px;
  }
`;

const CTASection = styled.div`
  background: #FFFFFF;
  padding: ${getSpacing('2xl')} ${getSpacing('xl')};
  text-align: center;
  margin-top: ${getSpacing('lg')};
  border-top: 1px solid ${getColor('primary.100')};
  
  @media (max-width: 768px) {
    padding: ${getSpacing('xl')} ${getSpacing('lg')};
  }
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTAButton = styled.button`
  padding: 14px 32px;
  font-size: 0.75rem;
  font-weight: 500;
  font-family: ${DesignSystem.typography.fonts.primary};
  text-transform: uppercase;
  letter-spacing: 0.2em;
  background: #CAB96E;
  border-radius: 50px;
  border: 1px solid #CAB96E;
  color: #1a1a1a;
  cursor: pointer;
  box-shadow: none;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  
  &:hover {
    background: #BAA544;
    border-color: #BAA544;
    transform: translateY(-2px);
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const BackButton = styled.button`
  padding: 8px 16px;
  font-size: 0.75rem;
  font-weight: 500;
  background: transparent;
  border: 1px solid ${getColor('primary.300')};
  color: ${getColor('primary.700')};
  border-radius: 6px;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ease;
  margin-bottom: ${getSpacing('lg')};
  
  &:hover {
    background: ${getColor('primary.100')};
    border-color: ${getColor('primary.400')};
  }
  
  &:focus-visible {
    outline: 2px solid #CAB96E;
    outline-offset: 2px;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: ${getSpacing('4xl')} 0;
  color: ${getColor('primary.600')};
  font-size: 1rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${getSpacing('4xl')} ${getSpacing('2xl')};
  color: ${getColor('primary.600')};
  
  h3 {
    font-size: 1.65rem;
    margin: 0 0 ${getSpacing('sm')} 0;
    color: ${getColor('primary.700')};
  }
  
  p {
    font-size: 1rem;
    margin: 0;
    line-height: 1.6;
  }
`;

const CuratedExperiences: React.FC<CuratedExperiencesProps> = ({ 
  destination, 
  onBack, 
  onNavigateTo,
  tourType 
}) => {
  const [itineraries, setItineraries] = React.useState<CombinedItinerary[]>([]);
  const [filteredItineraries, setFilteredItineraries] = React.useState<CombinedItinerary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const mapStateToFilter = (state?: string): StateFilter => {
    if (!state) return 'all';
    const s = state.toLowerCase();
    if (s.includes('ladakh')) return 'Ladakh';
    if (s.includes('kashmir') || s.includes('jammu')) return 'Kashmir';
    if (s.includes('himachal')) return 'Himachal';
    return 'all';
  };

  const [selectedState, setSelectedState] = React.useState<StateFilter>(
    mapStateToFilter(destination?.state)
  );
  const [selectedDuration, setSelectedDuration] = React.useState<string | null>(null);
  const [selectedTourType, setSelectedTourType] = React.useState<TourTypeFilter>(tourType || 'all');
  const contentRef = useRef<HTMLDivElement>(null);

  const scrollToContent = useCallback(() => {
    contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ─── Header dropdown data ─────────────────────────────────────────────────
  const { destinationItineraries, trekkingItineraries, bikeItineraries } = useHeaderItineraries();

  // FlipCard state
  const [flippedCards, setFlippedCards] = React.useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = React.useState(false);
  const [isEnquiryOpen, setIsEnquiryOpen] = React.useState(false);
  const cardGridRef = React.useRef<HTMLDivElement>(null);

  // Auto-popup EnquiryDialog after 20s (once per session)
  useEffect(() => {
    if (sessionStorage.getItem('enquiry_dialog_shown')) return;
    const timer = setTimeout(() => {
      setIsEnquiryOpen(true);
      sessionStorage.setItem('enquiry_dialog_shown', '1');
    }, 20000);
    return () => clearTimeout(timer);
  }, []);

  // Detect mobile for tap-to-flip
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-flip even-indexed cards when card grid scrolls into view
  const curatedCardIds = React.useMemo(
    () => filteredItineraries.map((it: CombinedItinerary) => it.id),
    [filteredItineraries]
  );
  useAutoFlipCardsGrid(cardGridRef, curatedCardIds, setFlippedCards);

  // Journey card images for itineraries
  const journeyImages = [
    `${API_BASE_URL}/api/images/curatedexperience/dalhousie.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/hamptra_trek.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/hikkim_post_office.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/key_monastery.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/parashar_lake.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/shimlamall_road.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/tirthan_valley.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/dalhousie.jpg`,
    `${API_BASE_URL}/api/images/curatedexperience/hamptra_trek.jpg`
  ];

  const fetchItineraries = useCallback(async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching all itineraries');
      const allItineraries: CombinedItinerary[] = [];
      
      // Fetch ALL car + bike itineraries in a single call (no state filter)
      try {
        const data = await ItineraryAPI.getAvailableItineraries();
        allItineraries.push(...data);
        console.log('🚗🏍️ Loaded car+bike itineraries:', data.length);
      } catch (itinError) {
        console.warn('Failed to fetch itineraries:', itinError);
      }
      
      // Fetch treks (all states at once)
      try {
        const treks = await ItineraryAPI.getAvailableTreks();
        allItineraries.push(...treks);
        console.log('🏔️ Loaded treks:', treks.length);
      } catch (trekError) {
        console.warn('Failed to fetch treks:', trekError);
      }
      
      // Deduplicate by ID (safety measure)
      const uniqueMap = new Map<string, CombinedItinerary>();
      for (const item of allItineraries) {
        if (!uniqueMap.has(item.id)) {
          uniqueMap.set(item.id, item);
        }
      }
      const uniqueItineraries = Array.from(uniqueMap.values());
      
      console.log('✅ Total unique items:', uniqueItineraries.length);
      const tourTypeCounts = uniqueItineraries.reduce((acc, i) => {
        const t = i.tour_type || 'car';
        acc[t] = (acc[t] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      console.log('📊 Tour type breakdown:', tourTypeCounts);
      
      setItineraries(uniqueItineraries);
      setFilteredItineraries(uniqueItineraries);
      setError(null);
    } catch (err) {
      console.error('Error fetching itineraries:', err);
      setError('Failed to load curated experiences. Please try again.');
      setItineraries([]);
      setFilteredItineraries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log('📍 CuratedExperiences mounted with:', { 
      hasDestination: !!destination, 
      title: destination?.title,
      state: destination?.state 
    });
    fetchItineraries();
    window.scrollTo(0, 0);
  }, [destination, fetchItineraries]);

  // Filter itineraries by tour type, state and duration
  useEffect(() => {
    let filtered = itineraries;

    // Apply tour type filter
    if (selectedTourType !== 'all') {
      filtered = filtered.filter(itinerary => {
        return (itinerary.tour_type || 'car') === selectedTourType;
      });
    }

    // Apply state filter
    if (selectedState !== 'all') {
      filtered = filtered.filter(itinerary => {
        const itineraryStates = itinerary.state || [];
        // Check if the selected state is in the itinerary's states (case-insensitive)
        // Also map "Kashmir" to match both "Kashmir" and "Jammu" in data
        return itineraryStates.some(s => {
          const sLower = s.toLowerCase();
          const filterLower = selectedState.toLowerCase();
          if (filterLower === 'kashmir') {
            return sLower.includes('kashmir');
          }
          if (filterLower === 'himachal') {
            return sLower.includes('himachal');
          }
          if (filterLower === 'ladakh') {
            return sLower.includes('ladakh');
          }
          return sLower.includes(filterLower);
        });
      });
    }

    // Apply duration filter
    if (selectedDuration) {
      filtered = filtered.filter(itinerary => {
        const duration = itinerary.duration;
        if (selectedDuration === '3-5') return duration >= 3 && duration <= 5;
        if (selectedDuration === '6-8') return duration >= 6 && duration <= 8;
        if (selectedDuration === '9+') return duration >= 9;
        return true;
      });
    }

    setFilteredItineraries(filtered);
  }, [selectedTourType, selectedState, selectedDuration, itineraries]);

  const durationOptions = ['3-5', '6-8', '9+'];

  // If no destination provided (accessed from homepage), show all experiences
  const displayDestination = destination || {
    id: 0,
    title: 'Himalayan',
    description: 'All regions',
    category: 'all',
    duration: '',
    image: '',
    destinations: [],
    state: 'all'
  };

  return (
    <PageContainer>
      <AppHeader
        onNavigateTo={onNavigateTo}
        currentPage="curated-experiences"
        transparent
        destinationItineraries={destinationItineraries}
        trekkingItineraries={trekkingItineraries}
        bikeItineraries={bikeItineraries}
      />

      {/* Hero Section */}
      <DarkTextHeroWrapper>
      <CinematicHero
        bgImage={`${API_BASE_URL}/api/images/curatedexperience/pangong.webp`}
        height="100vh"
        mobileHeight="100vh"
        brightness={0.9}
        contrast={1.1}
        title={<>Curated <span>Experiences</span></>}
        subtitle={`Discover handcrafted ${displayDestination.title} itineraries tailored to your pace and preferences. From quick escapes to deep dives, find your perfect adventure.`}
      >
        <HeroFilterWrapper>
          <FiltersGroup>
            <FilterRow>
              <FilterGroupLabel>Type:</FilterGroupLabel>
              <FilterButton isActive={selectedTourType === 'all'} onClick={() => setSelectedTourType('all')}>All</FilterButton>
              <FilterButton isActive={selectedTourType === 'car'} onClick={() => setSelectedTourType('car')}>Car</FilterButton>
              <FilterButton isActive={selectedTourType === 'bike'} onClick={() => setSelectedTourType('bike')}>Bike</FilterButton>
              <FilterButton isActive={selectedTourType === 'trek'} onClick={() => setSelectedTourType('trek')}>Treks</FilterButton>
            </FilterRow>
            <FilterRow>
              <FilterGroupLabel>Duration:</FilterGroupLabel>
              <FilterButton isActive={selectedDuration === null} onClick={() => setSelectedDuration(null)}>All</FilterButton>
              {durationOptions.map(option => (
                <FilterButton key={option} isActive={selectedDuration === option} onClick={() => setSelectedDuration(option)}>{option} Days</FilterButton>
              ))}
            </FilterRow>
            <FilterRow>
              <FilterGroupLabel>Destination:</FilterGroupLabel>
              <FilterButton isActive={selectedState === 'all'} onClick={() => setSelectedState('all')}>All</FilterButton>
              <FilterButton isActive={selectedState === 'Ladakh'} onClick={() => setSelectedState('Ladakh')}>Ladakh</FilterButton>
              <FilterButton isActive={selectedState === 'Kashmir'} onClick={() => setSelectedState('Kashmir')}>Kashmir</FilterButton>
              <FilterButton isActive={selectedState === 'Himachal'} onClick={() => setSelectedState('Himachal')}>Himachal</FilterButton>
            </FilterRow>
          </FiltersGroup>
          <HeroResultsInfo>
            <HeroResultsTitle>Where Will You Wander?</HeroResultsTitle>
            <ScrollHintWrapper onClick={scrollToContent}>
              <ScrollHintText>↓ swipe down to uncover {filteredItineraries.length} curated adventure{filteredItineraries.length !== 1 ? 's' : ''} ↓</ScrollHintText>
              <BounceChevron>⌄</BounceChevron>
            </ScrollHintWrapper>
          </HeroResultsInfo>
        </HeroFilterWrapper>
      </CinematicHero>
      </DarkTextHeroWrapper>

      {/* Main Content */}
      <Container ref={contentRef}>
        <ContentWrapper>
          {/* Journeys Grid */}
          <SectionContainer>

            {loading && <LoadingSpinner>Loading experiences...</LoadingSpinner>}
            
            {error && (
              <EmptyState>
                <h3>Oops!</h3>
                <p>{error}</p>
              </EmptyState>
            )}

            {!loading && filteredItineraries.length === 0 && (
              <EmptyState>
                <h3>No experiences found</h3>
                <p>Try adjusting your filters to see more options.</p>
              </EmptyState>
            )}

            {!loading && filteredItineraries.length > 0 && (
              <CardGrid ref={cardGridRef}>
                {filteredItineraries.map((itinerary) => (
                  <FlipCard
                    key={itinerary.id}
                    item={itinerary}
                    variant={itinerary.tour_type === 'trek' ? 'trek' : 'journey'}
                    index={itinerary.id}
                    isFlipped={flippedCards.has(itinerary.id)}
                    isMobile={isMobile}
                    onFlip={(id) => {
                      setFlippedCards(prev => {
                        const next = new Set(prev);
                        if (next.has(id as string)) {
                          next.delete(id as string);
                        } else {
                          next.add(id as string);
                        }
                        return next;
                      });
                    }}
                    onExplore={(item) => {
                      if (item.tour_type === 'trek') {
                        onNavigateTo && onNavigateTo('trek-detail', {
                          state: item.state?.[0] || 'Ladakh',
                          itineraryId: item.id,
                          fromDestination: destination || undefined,
                          fromCuratedExperiences: true
                        });
                      } else {
                        onNavigateTo && onNavigateTo('detailed-itinerary', {
                          state: item.state?.[0] || 'Ladakh',
                          itineraryId: item.id,
                          fromDestination: destination || undefined,
                          fromCuratedExperiences: true
                        });
                      }
                    }}
                  />
                ))}
              </CardGrid>
            )}
          </SectionContainer>

        </ContentWrapper>
      </Container>

      <EnquiryDialog
        isOpen={isEnquiryOpen}
        onClose={() => setIsEnquiryOpen(false)}
        sourcePage="curated-experiences"
      />
    </PageContainer>
  );
};

export default CuratedExperiences;
